import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import Header from "../components/Header";
import ImageCropper from "../components/ImageCropper";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { User, Mail, Shield, Save, Camera } from "lucide-react";
import { toast } from "sonner";
import { getImageUrl, getInitials } from "../lib/avatarUtils";

export default function AdminProfilePage() {
  const { trainer, isAdmin, logout, setTrainer } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);

  useEffect(() => {
    if (!trainer || !isAdmin) {
      navigate("/dashboard", { replace: true });
      return;
    }
    setName(trainer.name || "");
  }, [trainer, isAdmin, navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/profile', { name });
      setTrainer(res.data);
      toast.success("Profile updated!");
    } catch {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setCropImage(reader.result);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
    if (photoInputRef.current) photoInputRef.current.value = "";
  };

  const handleCroppedPhoto = async (blob) => {
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      const ext = blob.type === "image/png" ? "png" : "jpg";
      formData.append("file", blob, `profile.${ext}`);
      const res = await api.post('/profile/upload-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setTrainer(res.data);
      toast.success("Photo updated!");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploadingPhoto(false);
    }
  };

  if (!trainer) return null;

  const photoUrl = trainer.profile_photo
    ? getImageUrl(trainer.profile_photo)
    : null;

  return (
    <div className="min-h-screen" style={{ background: "#050505" }}>
      <Header title="Admin Profile" />
      <main className="container mx-auto px-4 py-6 max-w-xl">
        {/* Profile Photo */}
        <div className="flex justify-center mb-6">
          <div className="relative group" data-testid="admin-photo-area">
            <div
              className="w-28 h-28 rounded-full overflow-hidden border-2 border-white/10 cursor-pointer hover:border-[#00BFFF]/50 transition-all"
              onClick={() => photoInputRef.current?.click()}
            >
              {photoUrl ? (
                <img src={photoUrl} alt="Admin" className="w-full h-full object-cover" data-testid="admin-profile-photo" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#00BFFF]/20 to-[#2563EB]/20 flex items-center justify-center">
                  <span className="font-heading text-3xl font-bold text-[#00BFFF]" data-testid="admin-profile-initials">{getInitials(trainer.name)}</span>
                </div>
              )}
            </div>
            <button
              onClick={() => photoInputRef.current?.click()}
              className="absolute bottom-0 right-0 w-9 h-9 rounded-full bg-[#00BFFF] flex items-center justify-center text-white shadow-lg hover:bg-[#00BFFF]/80 transition-all"
              data-testid="admin-photo-upload-btn"
            >
              <Camera className="w-4 h-4" />
            </button>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoSelect}
              className="hidden"
              data-testid="admin-photo-input"
            />
          </div>
        </div>

        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="font-heading text-sm uppercase tracking-widest flex items-center gap-2">
              <Shield className="w-4 h-4 text-[#00BFFF]" /> Admin Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-11 bg-secondary/50 border-transparent"
                data-testid="admin-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Email</Label>
              <div className="flex items-center gap-2 p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-foreground" data-testid="admin-email">{trainer.email}</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Role</Label>
              <div className="flex items-center gap-2 p-3 rounded-xl bg-[#00BFFF]/5 border border-[#00BFFF]/10">
                <Shield className="w-4 h-4 text-[#00BFFF]" />
                <span className="text-sm text-[#00BFFF] font-medium">Administrator</span>
              </div>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full h-11 btn-premium text-white font-semibold text-xs uppercase tracking-widest rounded-xl"
              data-testid="admin-save-btn"
            >
              {saving ? "Saving..." : <><Save className="w-4 h-4 mr-2" /> Save Changes</>}
            </Button>
          </CardContent>
        </Card>
      </main>

      <ImageCropper
        open={showCropper}
        onClose={() => { setShowCropper(false); setCropImage(null); }}
        imageSrc={cropImage}
        onCropComplete={handleCroppedPhoto}
        aspectRatio={1}
      />
    </div>
  );
}
